import { useState } from 'react'
import { REPO_NAME, REPO_OWNER } from '../../github/config'

export function SignIn({
  onSubmit,
  error,
  busy,
}: {
  onSubmit: (token: string) => void
  error: string | null
  busy: boolean
}) {
  const [token, setToken] = useState('')
  return (
    <div className="mx-auto flex min-h-screen max-w-lg flex-col justify-center gap-4 p-6">
      <h1 className="text-2xl font-semibold text-parchment">Kingsmith Quest Editor</h1>
      <p className="text-sm text-dim">
        Edits are committed straight to{' '}
        <code className="font-mono">
          {REPO_OWNER}/{REPO_NAME}
        </code>
        . You need a GitHub personal access token — not git, just the token.
      </p>

      <ol className="list-decimal space-y-1 rounded border border-edge bg-ink-deep p-4 pl-8 text-sm text-parchment">
        <li>
          Open{' '}
          <a
            className="text-gold underline"
            href="https://github.com/settings/personal-access-tokens/new"
            target="_blank"
            rel="noreferrer"
          >
            github.com/settings/personal-access-tokens/new
          </a>
          . (Developer settings lives at the bottom of your{' '}
          <em>personal account</em> settings — not a repository's settings.)
        </li>
        <li>
          Set <em>Resource owner</em> to{' '}
          <code className="font-mono text-xs">{REPO_OWNER}</code>. This step is easy to
          miss — leaving it on your own username produces a token that cannot see the
          repo at all.
        </li>
        <li>
          Under <em>Repository access</em> choose <strong>Only select repositories</strong>{' '}
          and pick <code className="font-mono text-xs">{REPO_NAME}</code>.
        </li>
        <li>
          Under <em>Permissions → Repository permissions</em>, set{' '}
          <strong>Contents</strong> to <strong>Read and write</strong>. Also set{' '}
          <strong>Pull requests</strong> to <strong>Read and write</strong> if you want to
          propose changes for review.
        </li>
        <li>Generate it, copy it, and paste it below.</li>
      </ol>

      <form
        onSubmit={(event) => {
          event.preventDefault()
          if (token.trim()) onSubmit(token.trim())
        }}
        className="flex flex-col gap-2"
      >
        <input
          type="password"
          autoComplete="off"
          placeholder="github_pat_…"
          className="rounded border border-edge px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-gold"
          value={token}
          onChange={(event) => setToken(event.target.value)}
        />
        <button
          type="submit"
          disabled={busy || !token.trim()}
          className="rounded bg-gold px-4 py-2 text-sm font-medium text-ink hover:bg-gold-lit disabled:bg-edge disabled:text-dim"
        >
          {busy ? 'Checking…' : 'Sign in'}
        </button>
      </form>

      {error && (
        <p className="rounded border border-danger-edge bg-danger-bg p-3 text-sm text-danger">{error}</p>
      )}

      <p className="text-xs leading-relaxed text-dim">
        The token is kept in this browser's local storage so you don't have to paste it
        every time. It's scoped to this one repo — treat it like a password for the quest
        data, and use <strong>Sign out</strong> on a shared machine. Fine-grained tokens
        expire; when yours does, generate a new one and sign in again.
      </p>
    </div>
  )
}
